/**
 * Audio Generation Dialog Component
 * 
 * Modal dialog for triggering audio generation with voice selection.
 * Supports both monologue (single voice) and dialogue (voice mapping) clips.
 */

import logger from '../../core/logger.js';
import { adminClipsService } from '../../services/admin-clips-service.js';
import { AdminClip } from '../../types/admin-clip.js';
import { Voice } from '../../types/voice.js';
import toast from '../../ui/toast.js';

/**
 * Audio generation dialog callbacks
 */
interface AudioGenerationCallbacks {
  onJobQueued: (jobId: string) => void;
  onCancel: () => void;
}

/**
 * Show audio generation dialog
 */
export async function showAudioGenerationDialog(
  clip: AdminClip,
  callbacks: AudioGenerationCallbacks
): Promise<void> {
  logger.info('admin: Showing audio generation dialog', { clipId: clip.id, format: clip.format });
  
  // Fetch available voices
  let voices: Voice[] = [];
  
  try {
    const voicesResponse = await adminClipsService.fetchVoices(clip.language);
    voices = voicesResponse.voices || [];
    
    if (voices.length === 0) {
      logger.error('admin: No voices available', { language: clip.language });
      toast.error(`No voices available for language: ${clip.language.toUpperCase()}`);
      return;
    }
  } catch (error) {
    logger.error('admin: Failed to fetch voices', { error });
    toast.error('Failed to load available voices');
    return;
  }
  
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'audio-generation-modal-overlay';
  
  // Create modal based on clip format
  const modal = document.createElement('div');
  modal.className = 'modal';
  
  if (clip.format === 'dialogue') {
    modal.innerHTML = createDialogueModalHTML(clip, voices);
  } else {
    modal.innerHTML = createMonologueModalHTML(voices);
  }
  
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // Add event listeners
  setupEventListeners(overlay, clip, callbacks);
}

/**
 * Create HTML for monologue clip modal
 */
function createMonologueModalHTML(voices: Voice[]): string {
  return `
    <div class="modal-header">
      <h2 class="modal-title">Generate Audio</h2>
      <button class="modal-close" id="close-audio-modal" aria-label="Close">×</button>
    </div>
    
    <div class="modal-body">
      <p class="modal-description">
        Select a voice to generate audio for this monologue clip.
      </p>
      
      <div class="form-group">
        <label for="voice-select">Voice</label>
        <select id="voice-select" class="form-input">
          <option value="">Select a voice...</option>
          ${voices.map(v => `
            <option value="${escapeHtml(v.id)}">
              ${escapeHtml(v.name)} (${escapeHtml(v.gender)}) - ${escapeHtml(v.description)}
            </option>
          `).join('')}
        </select>
      </div>
    </div>
    
    <div class="modal-footer">
      <button class="btn btn--secondary" id="cancel-audio">Cancel</button>
      <button class="btn btn--primary" id="submit-audio" disabled>
        Generate Audio
      </button>
    </div>
  `;
}

/**
 * Create HTML for dialogue clip modal
 */
function createDialogueModalHTML(clip: AdminClip, voices: Voice[]): string {
  const speakers = clip.speakers || [];
  
  if (speakers.length === 0) {
    return `
      <div class="modal-header">
        <h2 class="modal-title">Generate Audio</h2>
        <button class="modal-close" id="close-audio-modal" aria-label="Close">×</button>
      </div>
      
      <div class="modal-body">
        <p class="modal-description error">
          This dialogue clip has no speakers defined. Please add speakers before generating audio.
        </p>
      </div>
      
      <div class="modal-footer">
        <button class="btn btn--secondary" id="cancel-audio">Close</button>
      </div>
    `;
  }
  
  return `
    <div class="modal-header">
      <h2 class="modal-title">Generate Audio - Dialogue</h2>
      <button class="modal-close" id="close-audio-modal" aria-label="Close">×</button>
    </div>
    
    <div class="modal-body">
      <p class="modal-description">
        Assign a voice to each speaker in this dialogue clip.
        All speakers must have a voice assigned before generating audio.
      </p>
      
      ${speakers.map((speaker, index) => {
        const speakerName = typeof speaker === 'object' ? speaker.name : speaker;
        const speakerGender = typeof speaker === 'object' ? speaker.gender : undefined;
        
        // Filter voices by speaker gender if available
        const filteredVoices = speakerGender 
          ? voices.filter(v => v.gender === speakerGender)
          : voices;
        
        return `
          <div class="form-group">
            <label for="voice-select-${index}">
              ${escapeHtml(speakerName)}${speakerGender ? ` (${speakerGender})` : ''}
            </label>
            <select id="voice-select-${index}" class="form-input voice-mapping-select" data-speaker="${escapeHtml(speakerName)}">
              <option value="">Select a voice...</option>
              ${filteredVoices.map(v => `
                <option value="${escapeHtml(v.id)}">
                  ${escapeHtml(v.name)} (${escapeHtml(v.gender)}) - ${escapeHtml(v.description)}
                </option>
              `).join('')}
            </select>
          </div>
        `;
      }).join('')}
      
      <div class="validation-message" id="voice-validation-message" style="display: none; color: var(--color-error); margin-top: 1rem;">
        Please assign a voice to all speakers before submitting.
      </div>
    </div>
    
    <div class="modal-footer">
      <button class="btn btn--secondary" id="cancel-audio">Cancel</button>
      <button class="btn btn--primary" id="submit-audio" disabled>
        Generate Audio
      </button>
    </div>
  `;
}

/**
 * Setup event listeners for the modal
 */
function setupEventListeners(
  overlay: HTMLElement,
  clip: AdminClip,
  callbacks: AudioGenerationCallbacks
): void {
  const modal = overlay.querySelector('.modal');
  if (!modal) return;
  
  const closeBtn = modal.querySelector('#close-audio-modal') as HTMLButtonElement;
  const cancelBtn = modal.querySelector('#cancel-audio') as HTMLButtonElement;
  const submitBtn = modal.querySelector('#submit-audio') as HTMLButtonElement;
  
  // Handle keyboard escape
  const escapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeHandler();
    }
  };
  
  // Close handlers
  const closeHandler = () => {
    // Remove escape key listener to prevent memory leaks
    document.removeEventListener('keydown', escapeHandler);
    overlay.remove();
    callbacks.onCancel();
  };
  
  closeBtn?.addEventListener('click', closeHandler);
  cancelBtn?.addEventListener('click', closeHandler);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeHandler();
  });
  
  // Add escape key listener
  document.addEventListener('keydown', escapeHandler);
  
  // Validation and submit handlers
  if (clip.format === 'dialogue') {
    setupDialogueValidation(modal, submitBtn);
    submitBtn?.addEventListener('click', () => handleDialogueSubmit(clip, modal, overlay, callbacks));
  } else {
    setupMonologueValidation(modal, submitBtn);
    submitBtn?.addEventListener('click', () => handleMonologueSubmit(clip, modal, overlay, callbacks));
  }
}

/**
 * Setup validation for monologue voice selection
 */
function setupMonologueValidation(modal: Element, submitBtn: HTMLButtonElement): void {
  const voiceSelect = modal.querySelector('#voice-select') as HTMLSelectElement;
  
  if (!voiceSelect) return;
  
  voiceSelect.addEventListener('change', () => {
    submitBtn.disabled = !voiceSelect.value;
  });
}

/**
 * Setup validation for dialogue voice mapping
 */
function setupDialogueValidation(modal: Element, submitBtn: HTMLButtonElement): void {
  const selects = modal.querySelectorAll('.voice-mapping-select') as NodeListOf<HTMLSelectElement>;
  const validationMessage = modal.querySelector('#voice-validation-message') as HTMLElement;
  
  const validateAllSelected = () => {
    const allSelected = Array.from(selects).every(select => select.value !== '');
    submitBtn.disabled = !allSelected;
    
    // Show validation message only when form has been interacted with and not all selected
    if (validationMessage) {
      const anySelected = Array.from(selects).some(select => select.value !== '');
      validationMessage.style.display = (anySelected && !allSelected) ? 'block' : 'none';
    }
  };
  
  selects.forEach(select => {
    select.addEventListener('change', validateAllSelected);
  });
}

/**
 * Handle monologue submission
 */
async function handleMonologueSubmit(
  clip: AdminClip,
  modal: Element,
  overlay: HTMLElement,
  callbacks: AudioGenerationCallbacks
): Promise<void> {
  const voiceSelect = modal.querySelector('#voice-select') as HTMLSelectElement;
  const voiceId = voiceSelect?.value;
  
  if (!voiceId) {
    toast.error('Please select a voice');
    return;
  }
  
  try {
    logger.info('admin: Queueing audio generation for monologue', { clipId: clip.id, voiceId });
    
    const result = await adminClipsService.triggerAudioGeneration(clip.id, { voiceId });
    
    overlay.remove();
    toast.success('Audio generation queued successfully');
    callbacks.onJobQueued(result.job.id);
  } catch (error) {
    logger.error('admin: Failed to queue audio generation', { error });
    toast.error('Failed to queue audio generation');
  }
}

/**
 * Handle dialogue submission
 */
async function handleDialogueSubmit(
  clip: AdminClip,
  modal: Element,
  overlay: HTMLElement,
  callbacks: AudioGenerationCallbacks
): Promise<void> {
  const selects = modal.querySelectorAll('.voice-mapping-select') as NodeListOf<HTMLSelectElement>;
  const voiceMapping: Record<string, string> = {};
  
  // Build voice mapping
  selects.forEach(select => {
    const speakerName = select.getAttribute('data-speaker');
    const voiceId = select.value;
    
    if (speakerName && voiceId) {
      voiceMapping[speakerName] = voiceId;
    }
  });
  
  // Final validation
  const speakers = clip.speakers || [];
  const allAssigned = speakers.every(speaker => {
    const speakerName = typeof speaker === 'object' ? speaker.name : speaker;
    return voiceMapping[speakerName];
  });
  
  if (!allAssigned) {
    const validationMessage = modal.querySelector('#voice-validation-message') as HTMLElement;
    if (validationMessage) {
      validationMessage.style.display = 'block';
    }
    toast.error('Please assign a voice to all speakers');
    return;
  }
  
  try {
    logger.info('admin: Queueing audio generation for dialogue', { clipId: clip.id, voiceMapping });
    
    const result = await adminClipsService.triggerAudioGeneration(clip.id, { voiceMapping });
    
    overlay.remove();
    toast.success('Audio generation queued successfully');
    callbacks.onJobQueued(result.job.id);
  } catch (error) {
    logger.error('admin: Failed to queue audio generation', { error });
    toast.error('Failed to queue audio generation');
  }
}

/**
 * Escape HTML for safe rendering
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
